import { useState } from "react";
import { ChevronRight, Plus, SlidersHorizontal, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { VariantPickerDialog } from "./VariantPickerDialog";

interface BonusProduct {
  id: string;
  name: string;
  originalPrice: number;
  discountedPrice: number;
  image: string;
  discount: string;
  hasVariants?: boolean;
  slug?: string;
  variants?: string[];
  variantLabel?: string;
}

interface BonusProductsProps {
  products: BonusProduct[];
  maxDisplay?: number;
  onAddProduct: (productId: string, variant?: string) => void;
  currency?: string;
}

export const BonusProducts = ({
  products,
  maxDisplay = 3,
  onAddProduct,
  currency = "€",
}: BonusProductsProps) => {
  const [variantProduct, setVariantProduct] = useState<BonusProduct | null>(null);
  const displayProducts = products.slice(0, maxDisplay);
  const hasMore = products.length > maxDisplay;

  if (products.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </span>
          Zvýhodnené produkty k nákupu
        </h3>
        {hasMore && (
          <Link
            to="/darceky"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
          >
            Zobraziť všetky ({products.length})
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {displayProducts.map((product) => (
          <div key={product.id} className="bonus-product-card group hover:scale-[1.02] transition-all duration-300">
            <div className="relative mb-2">
              <img
                src={product.image}
                alt={product.name}
                className="w-full aspect-square object-cover rounded-lg bg-secondary"
              />
              <span className="discount-badge absolute top-1.5 left-1.5">
                {product.discount}
              </span>
            </div>
            <h4 className="text-xs font-medium text-foreground line-clamp-2 mb-1.5 min-h-[32px]">
              {product.name}
            </h4>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold text-primary">
                {product.discountedPrice.toFixed(2).replace(".", ",")} {currency}
              </span>
              <span className="text-xs text-muted-foreground line-through">
                {product.originalPrice.toFixed(2).replace(".", ",")} {currency}
              </span>
            </div>
            <button
              onClick={() =>
                product.variants?.length
                  ? setVariantProduct(product)
                  : onAddProduct(product.id)
              }
              className="w-full py-1.5 px-3 rounded-xl bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-1 hover:shadow-md hover:shadow-primary/20 active:scale-95"
            >
              {product.variants?.length ? (
                <>
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Vybrať variant
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-90" />
                  Pridať
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {variantProduct && (
        <VariantPickerDialog
          open
          onOpenChange={(o) => !o && setVariantProduct(null)}
          name={variantProduct.name}
          image={variantProduct.image}
          variants={variantProduct.variants ?? []}
          variantLabel={variantProduct.variantLabel ?? "Variant"}
          confirmLabel="Pridať do košíka"
          priceNode={
            <p className="text-xs">
              <span className="text-primary font-bold">
                {variantProduct.discountedPrice.toFixed(2).replace(".", ",")} {currency}
              </span>{" "}
              <span className="text-muted-foreground line-through">
                {variantProduct.originalPrice.toFixed(2).replace(".", ",")} {currency}
              </span>
            </p>
          }
          onConfirm={(v) => {
            onAddProduct(variantProduct.id, v);
            setVariantProduct(null);
          }}
        />
      )}
    </div>
  );
};
